import React, { useState, useEffect, useMemo } from 'react';
import { uploadImage, getImageUrlById } from '../../api/images';
import { createGallery, updateGallery } from '../../api/gallery';
import type { Gallery } from '../../features/gallery/types';
import ClassDescriptionEditor from './ClassDescriptionEditor';

interface GalleryModalProps {
  show: boolean;
  onHide: () => void;
  onGallerySaved: () => void;
  item?: Gallery | null;
  userId: string;
}

const GalleryModal: React.FC<GalleryModalProps> = ({ show, onHide, onGallerySaved, item, userId }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    descriptionHtml: '',
    displayOrder: 0,
    isActive: true,
  });
  const [imageId, setImageId] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (item) {
      setFormData({
        title: item.title,
        description: item.description || '',
        descriptionHtml: item.descriptionHtml ?? '',
        displayOrder: item.displayOrder,
        isActive: item.isActive,
      });
      setImageId(item.imageId ?? null);
      setImageFile(null);
    } else {
      setFormData({
        title: '',
        description: '',
        descriptionHtml: '',
        displayOrder: 0,
        isActive: true,
      });
      setImageId(null);
      setImageFile(null);
    }
    setError(null);
  }, [item, show]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target as HTMLInputElement | HTMLTextAreaElement;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      if (!formData.title.trim()) {
        setError('Title is required');
        return;
      }

      let finalImageId: string | null = imageId;
      if (imageFile) {
        const uploaded = await uploadImage(imageFile);
        finalImageId = uploaded.id;
      }

      let descriptionHtml = formData.descriptionHtml;
      if (!descriptionHtml.trim()) {
        descriptionHtml = formData.descriptionHtml;
      }
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = descriptionHtml;
      const plainDescription = tempDiv.textContent || tempDiv.innerText || '';

      const payload = {
        title: formData.title.trim(),
        description: plainDescription.trim(),
        descriptionHtml: descriptionHtml.trim() || null,
        imageId: finalImageId,
        displayOrder: formData.displayOrder,
        isActive: formData.isActive,
      };

      if (item) {
        await updateGallery(item.id, payload);
      } else {
        await createGallery(payload);
      }
      onGallerySaved();
      handleClose();
    } catch (err) {
      console.error('Gallery save error:', err);
      setError('An error occurred while saving the gallery item');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      title: '',
      description: '',
      descriptionHtml: '',
      displayOrder: 0,
      isActive: true,
    });
    setImageId(null);
    setImageFile(null);
    setError(null);
    onHide();
  };

  const currentImageUrl = imageId ? getImageUrlById(imageId) : null;
  const blobPreviewUrl = useMemo(() => {
    if (!imageFile) return null;
    return URL.createObjectURL(imageFile);
  }, [imageFile]);
  useEffect(() => {
    return () => {
      if (blobPreviewUrl) URL.revokeObjectURL(blobPreviewUrl);
    };
  }, [blobPreviewUrl]);
  const imagePreviewUrl = blobPreviewUrl ?? currentImageUrl;

  if (!show) return null;

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title">
              <i className="bi bi-images me-2"></i>
              {item ? 'Edit Gallery Item' : 'Add New Gallery Item'}
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={handleClose}></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && (
                <div className="alert alert-danger" role="alert">
                  <i className="bi bi-exclamation-triangle me-2"></i>{error}
                </div>
              )}
              <div className="row">
                <div className="col-md-8">
                  <div className="mb-3">
                    <label htmlFor="title" className="form-label">
                      <i className="bi bi-tag me-2"></i>Title *
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="Enter gallery item title"
                      required
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Status</label>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="isActive"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                      />
                      <label className="form-check-label" htmlFor="isActive">
                        Active (show on home page)
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label">
                  <i className="bi bi-file-text me-2"></i>Description
                </label>
                <ClassDescriptionEditor
                  key={item?.id ?? 'new'}
                  html={formData.descriptionHtml}
                  onHtmlChange={(html) => setFormData((prev) => ({ ...prev, descriptionHtml: html }))}
                />
                <small className="text-muted">
                  This description will be shown alongside the image in the gallery.
                </small>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="displayOrder" className="form-label">
                      <i className="bi bi-sort-numeric-down me-2"></i>Display Order
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      id="displayOrder"
                      name="displayOrder"
                      value={formData.displayOrder}
                      onChange={handleInputChange}
                      min="0"
                    />
                    <small className="text-muted">Lower numbers appear first in the carousel.</small>
                  </div>
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label">
                  <i className="bi bi-image me-2"></i>Gallery Image
                </label>
                {imagePreviewUrl && (
                  <div className="mb-2 d-flex justify-content-center align-items-center">
                    <img
                      src={imagePreviewUrl}
                      alt="Gallery preview"
                      style={{
                        maxWidth: 200,
                        maxHeight: 150,
                        objectFit: 'cover',
                        objectPosition: 'center',
                        borderRadius: 8,
                        display: 'block',
                      }}
                    />
                  </div>
                )}
                <input
                  type="file"
                  className="form-control"
                  accept="image/png,image/jpeg,image/jpg"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) setImageFile(file);
                  }}
                />
                <small className="text-muted">PNG, JPEG or JPG. Max 10MB.</small>
                {(imageId || imageFile) && (
                  <button
                    type="button"
                    className="btn btn-link btn-sm text-danger p-0 mt-1 d-block"
                    onClick={() => { setImageId(null); setImageFile(null); }}
                  >
                    Remove image
                  </button>
                )}
              </div>
              <div className="alert alert-info" role="alert">
                <i className="bi bi-info-circle me-2"></i>
                <strong>Gallery Items:</strong> Active items will be displayed in the home page gallery carousel. Use the display order to control the sequence.
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={isLoading}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    {item ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle me-2"></i>
                    {item ? 'Update Item' : 'Create Item'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default GalleryModal;
